import mergeWith from 'lodash/mergeWith';

import { type JSONValue } from '../../types';

import { triggerStorageEvent } from './trigger-storage-event';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const mergeObjectDefaults = (
  parsedValue: Record<string, unknown>,
  defaultValue: Record<string, unknown>,
): Record<string, unknown> =>
  mergeWith({}, defaultValue, parsedValue, (_objValue, sourceValue) => {
    if (Array.isArray(sourceValue)) {
      return sourceValue;
    }
  });

export const createSessionStorageConfig = <V extends JSONValue>(
  initialSessionStorageData: V,
) => ({
  subscribe: (key: string, callback: (value: V) => void) => {
    const storageEventCallback = (e: StorageEvent) => {
      if (e.key === key && e.newValue) {
        callback(JSON.parse(e.newValue));
      }
    };

    window.addEventListener('storage', storageEventCallback);
    return () => {
      window.removeEventListener('storage', storageEventCallback);
    };
  },
  getItem: function (key: string): V {
    const value = sessionStorage.getItem(key);

    if (!value) {
      return initialSessionStorageData;
    }

    const parsedValue = JSON.parse(value);

    if (isRecord(parsedValue) && isRecord(initialSessionStorageData)) {
      return mergeObjectDefaults(parsedValue, initialSessionStorageData) as V;
    }

    return parsedValue as V;
  },
  setItem: function (key: string, newValue: V): void {
    const oldValue = sessionStorage.getItem(key);
    const value = JSON.stringify(newValue);
    sessionStorage.setItem(key, value);

    triggerStorageEvent(key, oldValue, value);
  },
  removeItem: function (key: string): void {
    const oldValue = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);

    triggerStorageEvent(key, oldValue, null);
  },
});
